import styles from './AddProperty.module.css';
import { useFormik } from 'formik';
import clsx from 'clsx';
import { useNavigate } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import {
  AddBuildingRequest,
  PersonalInfoRequest,
  UpdateBuildingRequest,
} from '../../../helpers/utils/types';
import {
  addBuilding,
  getBuildingDetails,
  getTenantDetails,
  updateBuilding,
  updatePersonalInfo,
} from '../../../auth/_requests';
import { useAuth } from '../../../auth/Auth';
import { AuthBrandedLoader } from '../../Login/AuthBrandedLoader';
import loginStyles from '../../Login/Login.module.css';
import { AuthPageShell } from '../../Login/AuthPageShell';
import { addProperty } from '../../../helpers/utils/addPropertyScehmas/addPropertyschema';
import { Tooltip, Whisper } from 'rsuite';
import { ChevronLeftIcon, Info } from 'lucide-react';
import {
  trackOnboardingCompletedOnce,
  trackOnboardingStepCompleted,
} from '../../../helpers/utils/analytics';
import { OccupationSelect } from '../../../helpers/components/OccupationSelect/OccupationSelect';
import { DueDateCalendar } from '../../../helpers/components/DueDateCalendar/DueDateCalendar';

const initialValues = {
  rent: '',
  dueDay: 1,
  occupation: '',
};
export const Dollar = () => {
  return (
    <svg xmlns='http://www.w3.org/2000/svg' width='24' height='24' viewBox='0 0 24 24' fill='none'>
      <path
        fillRule='evenodd'
        clipRule='evenodd'
        d='M12.0001 2.25C12.4143 2.25 12.7501 2.58579 12.7501 3V4.81396C14.2294 5.01204 15.736 5.67527 16.6842 6.6235C16.9771 6.91639 16.9771 7.39126 16.6842 7.68416C16.3913 7.97705 15.9165 7.97705 15.6236 7.68415C14.8319 6.89247 13.3551 6.29111 11.9799 6.2541C11.1703 6.23231 10.4934 6.40895 10.0372 6.75448C9.61238 7.07627 9.28854 7.61148 9.28854 8.53845C9.28854 8.9373 9.3782 9.22358 9.50976 9.444C9.64392 9.66876 9.84667 9.86652 10.1311 10.0475C10.7299 10.4286 11.5598 10.662 12.5436 10.9302C12.564 10.9358 12.5846 10.9414 12.6052 10.947C13.5113 11.194 14.556 11.4787 15.3666 11.9946C15.7962 12.2679 16.1884 12.6219 16.4707 13.0948C16.7556 13.5721 16.9039 14.1296 16.9039 14.7692C16.9039 16.2 16.2765 17.2754 15.3048 17.9559C14.5699 18.4706 13.6698 18.7397 12.7501 18.8129V20.9999C12.7501 21.4141 12.4143 21.7499 12.0001 21.7499C11.5858 21.7499 11.2501 21.4141 11.2501 20.9999V18.7659C9.71299 18.5519 8.16711 17.8316 7.24624 16.6038C6.99771 16.2724 7.06486 15.8023 7.39624 15.5538C7.72761 15.3053 8.19771 15.3724 8.44624 15.7038C9.16504 16.6622 10.5801 17.2783 12.027 17.3303C12.9765 17.3644 13.8407 17.15 14.4444 16.7272C15.0179 16.3256 15.4039 15.7074 15.4039 14.7692C15.4039 14.3703 15.3142 14.084 15.1827 13.8636C15.0485 13.6389 14.8458 13.4411 14.5613 13.2601C13.9626 12.8791 13.1326 12.6457 12.1489 12.3774C12.1284 12.3718 12.1079 12.3662 12.0872 12.3606C11.1811 12.1136 10.1364 11.8289 9.32578 11.313C8.89627 11.0397 8.50407 10.6858 8.22176 10.2128C7.93686 9.73551 7.78854 9.17805 7.78854 8.53845C7.78854 7.20847 8.28223 6.20206 9.13153 5.55876C9.74371 5.09508 10.488 4.85971 11.2501 4.78211V3C11.2501 2.58579 11.5858 2.25 12.0001 2.25Z'
        fill='currentColor'
      />
    </svg>
  );
};

const AddProperty = () => {
  const navigate = useNavigate();
  const { auth } = useAuth();
  const [loading, setLoading] = useState(false);
  const [dueDay, setDueDay] = useState<number>(1);
  const [formattedRent, setFormattedRent] = useState<string>('');
  const [accountType, setAccountType] = useState<string | undefined>();
  const [tenantBuildingId, setTenantBuildingId] = useState<string | undefined>();
  useEffect(() => {
    (async () => {
      const tenantDetails = await getTenantDetails(auth?.data?.token);
      setAccountType(tenantDetails?.data?.tenant?.accountType);
    })();
  }, [auth]);
  const formik = useFormik({
    initialValues,
    validationSchema: addProperty,
    onSubmit: async (values, { setStatus, setSubmitting }) => {
      setLoading(true);
      try {
        if (formik.isValid) {
          if (tenantBuildingId) {
            // Update existing building
            await updateBuilding(auth?.data.token, tenantBuildingId, {
              buildingDetails: {
                unitNumber: '', // keep as needed
                buildingName: '', // keep as needed
                rentAmount: Number(values.rent),
                dueDate: values.dueDay,
              },
            } as UpdateBuildingRequest);
          } else {
            // Create new building
            await addBuilding(auth?.data.token, {
              buildingDetails: {
                unitNumber: '',
                buildingName: '',
                rentAmount: Number(values.rent),
                dueDate: values.dueDay,
                type: accountType,
              },
            } as AddBuildingRequest);
            trackOnboardingStepCompleted('add_property');
            trackOnboardingCompletedOnce(auth?.data.tenantId);
          }
          // Update personal info (occupation)
          if (values.occupation) {
            await updatePersonalInfo(auth?.data.token, {
              tenantDetails: {
                occupation: values.occupation,
              },
            } as PersonalInfoRequest);
          }
          setStatus('');
          setLoading(false);

          navigate('/dashboard');
        } else {
          setStatus('Please fill all required fields');
          setLoading(false);
          setSubmitting(false);
        }
      } catch (error) {
        setStatus('Something went wrong');
        setLoading(false);
        setSubmitting(false);
      }
    },
  });
  const handleRentChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    let value = event.target.value;
    value = value.replace(/[^0-9.]/g, '');
    if (value && value !== '0') {
      const parts = value.split('.');
      if (parts.length === 2) {
        setFormattedRent(`${parseInt(parts[0], 10).toLocaleString()}.${parts[1]}`);
      } else {
        setFormattedRent(parseInt(value, 10).toLocaleString());
      }
      formik.setFieldValue('rent', value);
    } else {
      setFormattedRent(value);
      formik.setFieldValue('rent', value);
    }
  };
  useEffect(() => {
    const loadInitialData = async () => {
      try {
        const buildingRes = await getBuildingDetails(auth?.data.token);
        const buildings = buildingRes?.data;
        if (Array.isArray(buildings) && buildings.length > 0) {
          const buildingData = buildings[0];
          const rawRent = buildingData.building?.rentAmount;
          formik.setFieldValue('rent', rawRent != null ? String(rawRent) : '');
          setFormattedRent(rawRent != null ? Number(rawRent).toLocaleString() : '');
          setDueDay(buildingData.building?.dueDate || 1);
          formik.setFieldValue('dueDay', buildingData.building?.dueDate || 1);
          setTenantBuildingId(buildingData.building?.tenantBuildingId);
        }
        const tenantRes = await getTenantDetails(auth?.data.token);
        const occupation = tenantRes?.data?.tenant?.occupation;

        if (occupation) {
          formik.setFieldValue('occupation', occupation);
        }
      } catch (error) {
        console.error('Error checking:', error);
      }
    };
    loadInitialData();
  }, []);

  if (loading) {
    return <AuthBrandedLoader message='Loading…' />;
  }
  return (
    <AuthPageShell variant='center'>
      <div className={loginStyles.authFlowMax}>
        <motion.main
          className={`${loginStyles.main} ${loginStyles.mainWide} auth-onboarding-card max-w-full`}
          initial={false}
        >
          <button
            type='button'
            className={styles.headerOuter}
            onClick={() => {
              if (accountType === 'business') {
                navigate('/kyb/stakeholders');
              } else {
                navigate('/tenant-type');
              }
            }}
          >
            <ChevronLeftIcon className='text-white/70' stroke='currentColor' />
            <p className={styles.emailHeader}>One last step!</p>
          </button>

          <div className={styles.formOuter}>
            <form className={styles.form} noValidate onSubmit={formik.handleSubmit}>
              <div className={styles.formInner}>
                <div className={styles.formBoxes}>
                  <div className={styles.nameBox}>
                    <div className='inputWrapper' title='Enter rent amount'>
                      <label className={'label !text-[12px]'}>
                        Rent amount<span className={styles.mandatory}>*</span>
                      </label>
                      <div
                        className={clsx(
                          formik.touched.rent && formik.errors.rent ? styles.isInvalid : '',
                          formik.touched.rent && !formik.errors.rent ? styles.valid : '',
                          `inputField`
                        )}
                      >
                        {!!formik.values.rent && (
                          <span className='flex shrink-0 text-white'>
                            <Dollar />
                          </span>
                        )}
                        <input
                          type='text'
                          autoComplete='off'
                          {...formik.getFieldProps('rent')}
                          value={
                            formattedRent ||
                            (formik.values.rent !== ''
                              ? Number(formik.values.rent).toLocaleString()
                              : '')
                          }
                          placeholder='Enter your monthly rent'
                          onChange={handleRentChange}
                          onKeyDown={(event) => {
                            if (event.key === 'Enter') {
                              event.preventDefault();
                            }
                          }}
                        />
                      </div>
                      {formik.touched.rent && formik.errors.rent && (
                        <div className={styles.error}>
                          <span>{formik.errors.rent}</span>
                        </div>
                      )}
                    </div>
                    <div className={clsx('inputWrapper', styles.dueDate)}>
                      <div className={styles.dueDateLabelRow}>
                        <label className={'label !text-[12px]'}>
                          Monthly due date<span className={styles.mandatory}>*</span>
                        </label>
                        <Whisper
                          trigger='hover'
                          placement='top'
                          controlId='add-property-due-date-hint'
                          speaker={
                            <Tooltip>
                              <p className={styles.dueDateTooltipText}>
                                Select a day between the 1st and 28th, your landlord will receive the
                                rent payment by this day each month.
                              </p>
                            </Tooltip>
                          }
                        >
                          <button
                            type='button'
                            className={styles.dueDateHintBtn}
                            aria-label='About monthly due date'
                          >
                            <Info className={styles.dueDateHintIcon} strokeWidth={2} aria-hidden />
                          </button>
                        </Whisper>
                      </div>
                      <DueDateCalendar
                        selectedDay={dueDay}
                        onSelectDay={(day) => {
                          setDueDay(day);
                          formik.setFieldValue('dueDay', day);
                        }}
                        variant='dark'
                      />
                      {formik.touched.dueDay && formik.errors.dueDay && (
                        <div className={styles.error}>
                          <span>{formik.errors.dueDay}</span>
                        </div>
                      )}
                    </div>
                    <OccupationSelect
                      value={formik.values.occupation}
                      error={formik.errors.occupation}
                      touched={formik.touched.occupation}
                      onChange={(occupation) => formik.setFieldValue('occupation', occupation, true)}
                      onTouched={(touched) => formik.setFieldTouched('occupation', touched, false)}
                      onBlur={() => formik.setFieldTouched('occupation', true, true)}
                      variant='auth'
                      labelClassName='!text-[12px]'
                      validClassName={styles.valid}
                      invalidClassName={styles.isInvalid}
                      errorClassName={styles.error}
                      mandatoryClassName={styles.mandatory}
                    />
                  </div>
                </div>
              </div>

              <button
                type='submit'
                disabled={!formik.isValid}
                className={`${loginStyles.submitBtn} ${
                  (formik.isSubmitting || !formik.isValid) && loginStyles.disableBtn
                }`}
              >
                Next
              </button>
            </form>
          </div>
        </motion.main>
      </div>
    </AuthPageShell>
  );
};
export default AddProperty;
